/**
 * Row F's Campaign type and Campaign filters, and box 5's Active filter (spec
 * §4, §4.1), applied on the client with no read (spec §2.2). One index of the
 * channel's campaigns, built from the entities, answers every question: the
 * options (R25), which URL values apply (R11), whether A is on, and which rows
 * a filter keeps. Currency and objective filter the facts and the ads alike,
 * so the ad-grain tabs and the breakdown are always in one currency (FR-X-02).
 * Pure.
 *
 * @module services/channels/channelFilters
 */
import { BUY_TYPES, classifyCampaignName } from '@/services/looker/campaignNaming';
import type { ObjectiveFilter } from '@/services/overview/overviewTypes';
import { campaignStateOf, isActiveState } from './campaignState';
import { campaignTypeOf, idPrefix } from './channelNaming';
import {
  UNCLASSIFIED_TYPE,
  type AppliedFilters, type CampaignInfo, type CampaignOption, type ChannelAdRow, type ChannelEntity,
  type ChannelFilterState, type FilterOptions, type TypeOption,
} from './channelTypes';

const campaignInfoOf = (e: ChannelEntity, yesterday: string): CampaignInfo => ({
  id: e.campaignId,
  workspaceId: e.workspaceId,
  name: e.campaignName,
  type: campaignTypeOf(e.campaignName),
  conforms: classifyCampaignName(e.campaignName).conforms,
  state: campaignStateOf(e, yesterday),
  nativeId: e.campaignExternalId,
  accountExternalId: e.accountExternalId,
});

/** One entry per campaign id, from its first entity row (the campaign's fields repeat on every ad-set row). */
export const campaignIndexOf = (entities: readonly ChannelEntity[], yesterday: string): ReadonlyMap<string, CampaignInfo> => {
  // Built locally and handed out read-only; a copy per row would be quadratic.
  const index = new Map<string, CampaignInfo>();
  entities.forEach((e) => {
    if (!index.has(e.campaignId)) index.set(e.campaignId, campaignInfoOf(e, yesterday));
  });
  return index;
};

/**
 * The one "is this campaign Active" question (R16, R37): activeRows, effectiveActiveOf and box 5's hidden count
 * all ask it here, so "ซ่อน N" is exactly the campaigns A drops. A campaign the index does not hold has no state,
 * so it is not Active.
 */
export const isActiveIn = (index: ReadonlyMap<string, CampaignInfo>, campaignId: string): boolean => {
  const c = index.get(campaignId);
  return c !== undefined && isActiveState(c.state);
};

/** The types present, in BUY_TYPES order; no type last, when a campaign has none (R12). */
const typeOptionsOf = (campaigns: readonly CampaignInfo[]): TypeOption[] => {
  const present = new Set(campaigns.map((c) => c.type));
  const typed = BUY_TYPES.filter((b) => present.has(b)).map((b): TypeOption => ({ value: b, label: b }));
  return present.has(null) ? [...typed, { value: UNCLASSIFIED_TYPE, label: null }] : typed;
};

const campaignOptionOf = (
  c: CampaignInfo,
  workspaceNames: Readonly<Record<string, string>>,
  allWorkspaces: boolean,
): CampaignOption => ({
  value: c.id,
  // The Campaign table's fallback, the same idPrefix: the id's first 8 characters.
  name: c.name ?? idPrefix(c.id),
  workspaceName: allWorkspaces ? (workspaceNames[c.workspaceId] ?? null) : null,
  active: isActiveState(c.state),
});

const byName = (a: CampaignOption, b: CampaignOption): number =>
  a.name.localeCompare(b.name)
  || (a.workspaceName ?? '').localeCompare(b.workspaceName ?? '')
  || a.value.localeCompare(b.value);

/** R25: every campaign of the channel, spent or not, by name; the workspace name only in all workspaces. */
export const filterOptionsOf = (
  index: ReadonlyMap<string, CampaignInfo>,
  workspaceNames: Readonly<Record<string, string>>,
  allWorkspaces: boolean,
): FilterOptions => {
  const campaigns = [...index.values()];
  return {
    types: typeOptionsOf(campaigns),
    campaigns: campaigns.map((c) => campaignOptionOf(c, workspaceNames, allWorkspaces)).sort(byName),
  };
};

/** The URL values an option holds, once each, in URL order. */
const knownValues = (values: readonly string[], options: ReadonlyArray<{ value: string }>): string[] => {
  const allowed = new Set(options.map((o) => o.value));
  return [...new Set(values)].filter((v) => allowed.has(v));
};

/**
 * R11: applied = URL ∩ options. An id no campaign of this channel has (a link
 * from another workspace, a tab switched by hand) is dropped silently, and an
 * empty intersection is no filter (Review Focus 1).
 */
export const appliedFiltersOf = (
  f: Pick<ChannelFilterState, 'types' | 'campaigns'>,
  options: FilterOptions,
): AppliedFilters => ({
  types: knownValues(f.types, options.types),
  campaigns: knownValues(f.campaigns, options.campaigns),
});

/** R11: an explicit 1 / 0 wins. Otherwise A is on, unless an applied campaign is not Active: then off, so it shows. */
export const effectiveActiveOf = (
  active: boolean | null,
  applied: AppliedFilters,
  index: ReadonlyMap<string, CampaignInfo>,
): boolean => {
  if (active !== null) return active;
  // An id the index does not hold is not an applied campaign (Review Focus 1), so it never turns A off.
  return !applied.campaigns.some((id) => index.has(id) && !isActiveIn(index, id));
};

const isApplied = (applied: AppliedFilters): boolean => applied.types.length > 0 || applied.campaigns.length > 0;

/** Type AND campaign. Nothing applied → every campaign; a campaign missing from the index never matches a filter. */
export const campaignMatcherOf = (
  applied: AppliedFilters,
  index: ReadonlyMap<string, CampaignInfo>,
): ((campaignId: string) => boolean) => {
  if (!isApplied(applied)) return () => true;
  const types = new Set(applied.types);
  const campaigns = new Set(applied.campaigns);
  return (campaignId) => {
    const c = index.get(campaignId);
    if (c === undefined) return false;
    return (types.size === 0 || types.has(c.type ?? UNCLASSIFIED_TYPE))
      && (campaigns.size === 0 || campaigns.has(campaignId));
  };
};

/** "กรองเฉพาะ N แคมเปญ": the indexed campaigns that match; null when no type or campaign filter applies. */
export const filteredCampaignCountOf = (
  applied: AppliedFilters,
  index: ReadonlyMap<string, CampaignInfo>,
): number | null => {
  if (!isApplied(applied)) return null;
  return [...index.keys()].filter(campaignMatcherOf(applied, index)).length;
};

export interface RowFilters {
  /** The page currency; null = none known, so nothing qualifies (as D1). */
  currency: string | null;
  objective: ObjectiveFilter;
  /** campaignMatcherOf's answer for the type and campaign filters. */
  matches: (campaignId: string) => boolean;
}

/** Facts or ads under the currency, objective and campaign filters. A NULL-currency row matches no currency. */
export const filterChannelRows = <R extends Pick<ChannelAdRow, 'currency' | 'objective' | 'campaignId'>>(
  rows: readonly R[],
  f: RowFilters,
): R[] => {
  if (f.currency === null) return [];
  return rows.filter((r) =>
    r.currency === f.currency
    && (f.objective === 'all' || r.objective === f.objective)
    && f.matches(r.campaignId));
};

/** A: the rows whose campaign is Active. A campaign missing from the index is not (its state is unknown). */
export const activeRows = <R extends Pick<ChannelAdRow, 'campaignId'>>(
  rows: readonly R[],
  index: ReadonlyMap<string, CampaignInfo>,
): R[] => rows.filter((r) => isActiveIn(index, r.campaignId));
